/**
 * What the token search means, and everything this application can be asked.
 *
 * The box's syntax has two rules nobody guesses: the same field twice widens
 * the search while two different fields narrow it, and a `-` has to keep the
 * rows that never recorded the field or the minus sign would lie. Those were
 * documented in the README and in a placeholder nobody reads, so this panel
 * says them where the query is typed.
 *
 * The field table is generated from the catalog rather than written out, so it
 * cannot list a filter the application does not record — the same promise the
 * bar itself makes by rendering only what `fieldsFor` returns.
 */

import { fieldsFor, type HistoryApp, type HistoryField } from './historyQuery';

/** What a value for this field looks like, in the user's terms. */
function accepts(field: HistoryField): string {
  if (field.values) return field.values.join(' or ');
  switch (field.kind) {
    case 'id':
      return 'the start of an id';
    case 'int':
      return 'a whole number';
    default:
      return 'any part of the text';
  }
}

function Rule({ example, children }: { example: string; children: React.ReactNode }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3">
      <code className="font-mono text-[11px] text-indigo-700 whitespace-nowrap">{example}</code>
      <span className="text-[11px] text-slate-600">{children}</span>
    </li>
  );
}

export function HistoryFilterHelp({ app }: { app: HistoryApp }) {
  const fields = fieldsFor(app);
  const freeText = fields.filter((field) => field.freeText);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4 space-y-4">
      <section className="space-y-1.5">
        <h4 className="text-xs font-semibold text-slate-700">How the search reads</h4>
        <ul className="space-y-1.5">
          <Rule example="org:acme">
            Matches anywhere in the value. Capitalisation does not matter.
          </Rule>
          <Rule example="source:manual source:uvcamp">
            The <strong>same field twice means either one</strong>. Repeating a field widens the
            search.
          </Rule>
          <Rule example="source:manual version:5.3">
            <strong>Different fields must all match.</strong> Adding a second field narrows the
            search.
          </Rule>
          <Rule example="-source:stress">
            A leading <code className="font-mono">-</code> excludes. Rows that never recorded the
            field are kept, so a minus never hides what it cannot judge.
          </Rule>
          <Rule example={'org:"acme motors"'}>Quote a value that contains a space.</Rule>
          {freeText.length > 0 && (
            <Rule example="acme">
              A word with no field searches {freeText.map((field) => field.label.toLowerCase()).join(', ')}.
            </Rule>
          )}
        </ul>
      </section>

      <section className="space-y-1.5">
        <h4 className="text-xs font-semibold text-slate-700">
          What this history records ({fields.length} fields)
        </h4>
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {fields.map((field) => (
            <li key={field.name} className="min-w-0">
              <div className="flex items-baseline gap-2 min-w-0">
                <code className="font-mono text-[11px] text-indigo-700 shrink-0">
                  {field.name}:
                </code>
                <span className="text-[11px] text-slate-600 truncate">{field.label}</span>
              </div>
              <div className="text-[10px] text-slate-400 pl-1">
                {accepts(field)}
                {field.aliases.length > 0 && ` · also ${field.aliases.join(', ')}`}
                {field.hint && ` · ${field.hint}`}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
